/**
 * The submit path on the turn pipeline (HIG-5971, design §3): POST the turn,
 * normalize a non-OK response into the taxonomy, then drain the SSE stream
 * through the same pipeline the resume path uses. The facade owns everything
 * genuinely UI — optimistic commit timing, queue-decision UX, temp→real
 * chat-id migration — via `onEvent` and the returned completion. Failures
 * travel only on the Err channel: `haltOnErrorEvent` settles the turn at the
 * first failure, so a separate event-time error tap would fire at the same
 * moment the Result does (the resume path, which drains past failures, is the
 * one that needs `onTurnError`).
 */
import { Result, ResultAsync, err, ok } from 'neverthrow'
import type { TurnState } from './accumulator'
import {
  RAY_ID_UNAVAILABLE,
  backendTurnError,
  interruptReasonFrom,
  planRefusalFromErrorCode,
  rayIdFromHeaders,
  type ChatTurnError,
} from './errors'
import type { ChatSSEEvent } from './events'
import type { ChatRequestBody, EngineDeps, SubmitReportContext } from './services'
import { waitForBackoff } from './retry'
import { createStreamAttempt, monitorStreamStalls } from './sse'
import { runTurnStream, type ResumeCompletion, type TurnCheckpoint } from './turn-pipeline'
import { describeTurnErrorForTrace, settledTrace, type TurnScope } from './turn-trace'

const MAX_RECONNECT_ATTEMPTS = 3
const RECONNECT_BASE_MS = 500
const RECONNECT_MAX_MS = 10_000

export type SubmitCompletion = ResumeCompletion

export type SubmitParams = {
  /** The conversation id at POST time (a temp uuid for new chats). */
  readonly chatId: string
  /** Identity of this engine turn for telemetry; the facade mints it. */
  readonly turnId: string
  /** Submission facts for the failure report; the engine reports its own terminal failure. */
  readonly reportContext?: SubmitReportContext
  readonly body: ChatRequestBody
  /**
   * Initial fold state, carrying the submit-side context the fold stamps onto
   * its patches: modelId, userMessageId, placeholderClientId, attachedContext.
   */
  readonly seed: TurnState
  /** Passive per-event observer for the facade's UI side effects. */
  readonly onEvent?: (event: ChatSSEEvent) => void
  readonly canResume?: () => boolean
  readonly getResumeConversationId?: () => string | undefined
  readonly reconcileMessages?: (chatId: string) => Promise<void>
  /**
   * Fires per consumed frame with the new cursor, then with `undefined` once
   * the turn completes. A resume that follows this submit reads the published
   * cursor and replays only the tail.
   */
  readonly onLastEventId?: (lastEventId: number | undefined) => void
}

/** Stamp the POST response's ray id onto failures that carry none of their own. */
/**
 * The user-facing message in a non-OK JSON body. Our routes answer with
 * `{ error: string }`; a zod validation failure answers with the serialized
 * `ZodError` (`{ error: { issues: [{ message }] } }`), whose first issue
 * carries the schema's own message (e.g. an attachment size limit).
 */
function backendErrorMessage(parsed: unknown): string | undefined {
  const error = (parsed as { error?: unknown } | null)?.error
  if (typeof error === 'string') return error
  const issues = (error as { issues?: unknown } | null)?.issues
  if (!Array.isArray(issues)) return undefined
  const message = (issues[0] as { message?: unknown } | undefined)?.message
  return typeof message === 'string' ? message : undefined
}

function withResponseRayId(error: ChatTurnError, rayId: string): ChatTurnError {
  if (rayId === RAY_ID_UNAVAILABLE) return error
  if (error._tag === 'BackendTurnError' && error.rayId === RAY_ID_UNAVAILABLE) {
    return { ...error, rayId }
  }
  return error
}

/**
 * Submit a turn and drain its stream. Resolves `completed`/`interrupted` on
 * the Ok side; the first failure lands on the Err channel, ray-id stamped.
 */
export function submitTurn(
  deps: EngineDeps,
  params: SubmitParams,
  signal: AbortSignal,
): ResultAsync<SubmitCompletion, ChatTurnError> {
  const ctx: TurnScope = { phase: 'submit', chatId: params.chatId, turnId: params.turnId }

  const run = async (): Promise<Result<SubmitCompletion, ChatTurnError>> => {
    const checkpoint: TurnCheckpoint = { state: params.seed, terminal: false }
    let reconnectAttempts = 0
    let everRetried = false
    let lastResponseRayId = RAY_ID_UNAVAILABLE

    const resumeConversationId = () => params.body.conversationId ?? params.getResumeConversationId?.()
    const canResume = () => !!params.body.msgId && !!resumeConversationId() && (params.canResume?.() ?? true)

    while (true) {
      const attempt = createStreamAttempt(signal)
      const lastEventIdBeforeAttempt = checkpoint.lastEventId
      let disposeMonitor: (() => void) | undefined
      let retryableFailure: ChatTurnError | undefined
      let stalled = false

      try {
        const body = everRetried ? { ...params.body, conversationId: resumeConversationId() } : params.body
        const connected = await deps.api.startTurnStream(params.chatId, body, attempt.controller.signal, params.turnId)
        if (connected.isErr()) {
          if (signal.aborted) return ok({ _tag: 'interrupted', reason: interruptReasonFrom(signal) })
          deps.telemetry.trace({
            kind: 'connect:failed',
            ...ctx,
            attempt: reconnectAttempts + 1,
            cause: describeTurnErrorForTrace(connected.error),
          })
          // A failed challenge is the user's or the vendor's, not the network's.
          // Re-driving would re-open the puzzle on every reconnect attempt.
          if (connected.error._tag === 'ArkoseChallengeError') return err(connected.error)
          retryableFailure = connected.error
        } else {
          const response = connected.value
          lastResponseRayId = rayIdFromHeaders(response.headers)
          deps.telemetry.trace({
            kind: 'connect:response',
            ...ctx,
            attempt: reconnectAttempts + 1,
            status: response.status,
            rayId: lastResponseRayId,
          })

          if (!response.ok) {
            const body = await ResultAsync.fromPromise(response.json() as Promise<unknown>, () =>
              backendTurnError(ctx, { kind: 'non-json-response', message: '', rayId: lastResponseRayId }),
            )
            const failure = body.match(
              (parsed): ChatTurnError => {
                const errorMessage = backendErrorMessage(parsed)
                if (typeof errorMessage !== 'string') {
                  return backendTurnError(ctx, {
                    kind: 'malformed-error-body',
                    message: '',
                    rayId: lastResponseRayId,
                  })
                }
                const errorCode = (parsed as { errorCode?: string }).errorCode
                const planRefusal = planRefusalFromErrorCode(ctx, errorCode, errorMessage, lastResponseRayId)
                if (planRefusal) return planRefusal
                return backendTurnError(ctx, {
                  kind: 'error-response',
                  message: errorMessage,
                  errorCode,
                  rayId: lastResponseRayId,
                })
              },
              (error) => error,
            )
            if (response.status >= 500) retryableFailure = failure
            else return err(failure)
          } else {
            const monitored = monitorStreamStalls(response, () => {
              stalled = true
              attempt.controller.abort('stream-stalled')
            })
            disposeMonitor = monitored.dispose

            const outcome = await runTurnStream(
              {
                sink: deps.sink,
                telemetry: deps.telemetry,
                seedFor: () => undefined,
                onEvent: params.onEvent,
                onTurnError: () => {},
                haltOnErrorEvent: true,
                onLastEventId: params.onLastEventId,
              },
              ctx,
              monitored.response,
              attempt.controller.signal,
              checkpoint.state,
              checkpoint,
            )

            if (outcome.isErr() && outcome.error._tag !== 'SseConnectionError') {
              return outcome.mapErr((error) => withResponseRayId(error, lastResponseRayId))
            }
            if (checkpoint.terminal) {
              if (everRetried && checkpoint.lastEventId === lastEventIdBeforeAttempt) {
                await params.reconcileMessages?.(resumeConversationId() ?? params.chatId)
              }
              params.onLastEventId?.(undefined)
              return ok({ _tag: 'completed', state: checkpoint.state })
            }
            if (signal.aborted) return ok({ _tag: 'interrupted', reason: interruptReasonFrom(signal) })
            if (!canResume() && outcome.isOk() && !stalled) {
              return ok({ _tag: 'completed', state: checkpoint.state })
            }
            retryableFailure =
              outcome.isErr() && outcome.error._tag === 'SseConnectionError'
                ? withResponseRayId(outcome.error, lastResponseRayId)
                : {
                    _tag: 'SseConnectionError',
                    phase: 'submit',
                    chatId: params.chatId,
                    cause: new Error(stalled ? 'SSE stream stalled' : 'SSE stream closed before done'),
                  }
          }
        }
      } finally {
        disposeMonitor?.()
        attempt.dispose()
      }

      if (!canResume()) return err(retryableFailure!)
      if (checkpoint.lastEventId !== lastEventIdBeforeAttempt) reconnectAttempts = 0
      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) return err(retryableFailure!)

      reconnectAttempts += 1
      everRetried = true
      deps.telemetry.trace({
        kind: 'stream:reconnect',
        ...ctx,
        attempt: reconnectAttempts,
        cause: describeTurnErrorForTrace(retryableFailure!),
      })
      await waitForBackoff(reconnectAttempts, {
        baseMs: RECONNECT_BASE_MS,
        maxMs: RECONNECT_MAX_MS,
        signal,
      })
      if (signal.aborted) return ok({ _tag: 'interrupted', reason: interruptReasonFrom(signal) })
      // Cancelling a queued bubble never aborts the signal, so a cancel landing
      // during the wait is only visible here. Re-driving past it would resend a
      // message the user withdrew.
      if (!canResume()) return err(retryableFailure!)
    }
  }

  const startedAt = Date.now()
  return new ResultAsync(
    run().then((result) => {
      // Reported here, before the settle, so the failure lands on the turn's
      // span while it is still open. The facade only stamps the transcript.
      if (result.isErr()) deps.telemetry.report(result.error, { ...params.reportContext, turnId: ctx.turnId })
      deps.telemetry.trace(settledTrace(ctx, result, Date.now() - startedAt))
      return result
    }),
  )
}
